import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { refresh } from '../../services/auth.service';
import Spinner from '../../components/layout/Spinner';
import FadeIn from '../../utils/fadein';
import { FaEllipsisV, FaTimes } from 'react-icons/fa';
import { getCompanyCampaigns, addCompanyCampaign, getSellerCompanies } from '../../utils/api/admin';
import { Calendar, Plus } from 'lucide-react';

const initialCampaignState = {
  name: '',
  description: '',
  date_scheduled: '',
  subscriber_count: '',
  clicks: '',
  impressions: '',
  company_campaign_source_id: '',
  company_id: ''
};

function CompanyCampaigns() {
  const [loaded, setLoaded] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sellerCompanies, setSellerCompanies] = useState([]);
  const [pageBreakdown, setPageBreakdown] = useState([]);
  const [data, setData] = useState([]);
  const [showSaveBanner, setShowSaveBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCampaign, setNewCampaign] = useState(initialCampaignState);
  const [campaignSources] = useState([
    { id: 1, name: 'Beehiiv' },
    { id: 2, name: 'Substack' },
  ]);
  const imageUrl = process.env.REACT_APP_IMAGE_URL;
  const baseImageUrl = process.env.REACT_APP_BASE_IMAGE_URL;
  const screenshotUrl = process.env.REACT_APP_BASE_IMAGE_URL + 'companyscreenshots/';

      async function loadData(){
      let res = await getCompanyCampaigns();
      if (res.success){
        setData(res.data);
        setLoaded(true);
      }
    }

  useEffect(() => {
    async function loadData(){
      let res = await getCompanyCampaigns();
      if (res.success){
        setData(res.data);
        setLoaded(true);
      }
    }

    async function loadSellerCompanies(){
      let res = await getSellerCompanies();
      if (res.success){
        setSellerCompanies(res.data);
      }
    }

    loadSellerCompanies();
    loadData();
  }, []);

  function loadSaveBanner(){
    setShowSaveBanner(true);
    const timer = setTimeout(() => {
      setShowSaveBanner(false);
    }, 3000);
    return () => clearTimeout(timer);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);

    try {
      const res = await addCompanyCampaign(newCampaign);
      if (res.success) {
        setNewCampaign(initialCampaignState); // Reset form
        setShowAddForm(false);
        loadData();
        loadSaveBanner();
      }
    } catch (err) {
      console.error(err);
    }

    setSubmitting(false);
  }

  return (
    <>
      <Helmet>
        <title>LeadrPro - Campaigns</title>
      </Helmet>

      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">Company Campaigns {`(${data.length})`}</h1>
        <button
          onClick={() => setShowAddForm(true)}
          className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Campaign
        </button>
      </div>

      {loaded===false && (
        <Spinner />
      )}

      {loaded && (
         <div>
          <table className='admindashboard__table table'>
            <tr>
              <th>Date Scheduled</th>
              <th>Company</th>
              <th>Name</th>
              <th>Source</th>
              <th>Subscribers</th>
              <th>Clicks</th>
            </tr>

            {data.map((item, index) => (
              <tr key={index}>
                <td>
                  {new Date(item.date_scheduled).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  })}
                </td>

                <td>
                  {item.company_name}
                </td>
                <td>
                  {item.name}
                </td>

                <td>
                  {item.source_name}
                </td>

                <td>
                  {item.subscriber_count}
                </td>

                <td>
                  {item.clicks}
                </td>
              </tr>
            ))}
          </table>
       </div>
      )}

      {showAddForm && (
        <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-y-auto max-h-[90vh]">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold">Add New Campaign</h2>
                  <button onClick={() => setShowAddForm(false)} className="text-gray-400 hover:text-gray-600">
                    <FaTimes />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {/* Add companies dropdown before name field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Company</label>
                    <select
                      required
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                      value={newCampaign.company_id}
                      onChange={(e) => setNewCampaign({...newCampaign, company_id: e.target.value})}
                    >
                      <option value="">Select a company</option>
                      {[...sellerCompanies]
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(company => (
                          <option key={company.company_id} value={company.company_id}>
                            {company.name} (ID: {company.company_id})
                          </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      required
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                      value={newCampaign.name}
                      onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date Scheduled</label>
                    <input
                      type="date"
                      required
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                      value={newCampaign.date_scheduled}
                      onChange={(e) => setNewCampaign({...newCampaign, date_scheduled: e.target.value})}
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      required
                      rows={3}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                      value={newCampaign.description}
                      onChange={(e) => setNewCampaign({...newCampaign, description: e.target.value})}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Subscriber Count</label>
                    <input
                      type="number"
                      required
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                      value={newCampaign.subscriber_count}
                      onChange={(e) => setNewCampaign({...newCampaign, subscriber_count: e.target.value})}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Clicks</label>
                    <input
                      type="number"
                      required
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                      value={newCampaign.clicks}
                      onChange={(e) => setNewCampaign({...newCampaign, clicks: e.target.value})}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Impressions</label>
                    <input
                      type="number"
                      required
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                      value={newCampaign.impressions}
                      onChange={(e) => setNewCampaign({...newCampaign, impressions: e.target.value})}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Click Through Rate (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Campaign Source</label>
                    <select
                      required
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                      value={newCampaign.company_campaign_source_id}
                      onChange={(e) => setNewCampaign({...newCampaign, company_campaign_source_id: e.target.value})}
                    >
                      <option value="">Select a source</option>
                      {campaignSources.map(source => (
                        <option key={source.id} value={source.id}>{source.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="px-4 py-2 text-gray-700 hover:text-gray-900"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    {submitting ? 'Adding...' : 'Add Campaign'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {showModal && (
        <>
          <div className='overlay'></div>
          <div className='modal'>
            <div className='modal__close' onClick={() => setShowModal(false)}>
              <FaTimes />
            </div>

            <h2 className='modal__h2'>Admin Note</h2>

            <div className='mt-10'>
            
            </div>
          </div>
        </>
      )}

      {showSaveBanner && (
        <FadeIn delay={150} duration={300}>
          <div className='profileinfo__saved-banner'>
            Profile updated successfully!
          </div>
        </FadeIn>
      )}
    </>
  );
}

export default CompanyCampaigns;